import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/contexts/AuthContext";
import { useApi } from "@/hooks/useApi";

/**
 * Global studies stats cache that persists across all page navigations.
 *
 * This hook is designed to be called at the ProtectedLayout level
 * so the query NEVER unmounts while the user is authenticated.
 * This ensures useActiveStudyCount always has data to read from,
 * regardless of which page the user is on.
 *
 * Uses the lightweight /studies/stats endpoint instead of fetching
 * hundreds of full study objects. The query key ["studies", "stats"]
 * is shared with ActiveStudiesEnhanced — React Query deduplicates
 * automatically, so only one request is made.
 */
export function useGlobalStudiesCache() {
  const { authStatus } = useAuth();
  const isAuthenticated = authStatus === "authenticated";
  const api = useApi();

  return useQuery({
    queryKey: ["studies", "stats"],
    queryFn: () => api.getStudyStats(),
    enabled: isAuthenticated,
    staleTime: 30000,
    refetchInterval: 60000,
    refetchOnWindowFocus: true,
    gcTime: 5 * 60 * 1000,
  });
}

export type GlobalStudiesCacheResult = ReturnType<typeof useGlobalStudiesCache>;
